import {useI18n} from '@/i18n'
import {Textarea} from '@/components/ui/textarea'
import {FormField} from '@/components/shared/FormField'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'

const REASON_MAX_LENGTH = 1000

/**
 * Reusable reason {@code <Textarea>} for lifecycle / account-action dialogs
 * (spec §8.1.3 / §8.2.3). Mirrors {@code IdentityLifecycleDialog} reason
 * shape — 1000-char cap, helper text, char counter "0 / 1000" turning red at
 * the limit. Passed value/onChange so the parent dialog owns the reason state.
 */
export interface ReasonFieldProps {
    value: string
    onChange: (next: string) => void
    testIdPrefix: string
}

export function ReasonField({value, onChange, testIdPrefix}: ReasonFieldProps) {
    const i18n = useI18n()
    const length = value.length
    const atLimit = length >= REASON_MAX_LENGTH

    return (
        <FormField
            label={String(i18n.lifecycleEvents.dialog.reasonLabel)}
            htmlFor={`${testIdPrefix}-reason`}
        >
            <Textarea
                id={`${testIdPrefix}-reason`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                maxLength={REASON_MAX_LENGTH}
                placeholder={String(i18n.lifecycleEvents.dialog.reasonPlaceholder)}
                data-testid={`${testIdPrefix}-reason-input`}
            />
            <InlineRow gap="xs" justify="between">
                <MutedText size="xs">
                    {i18n.lifecycleEvents.dialog.reasonHelp}
                </MutedText>
                <span
                    className={atLimit ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}
                    data-testid={`${testIdPrefix}-reason-counter`}
                >
                    {String(i18n.lifecycleEvents.dialog.reasonCounter({count: length}))}
                </span>
            </InlineRow>
        </FormField>
    )
}
